import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { OMSView } from '@src/components/OMS/OMSView';
import { useFeatureFlag } from '@src/hooks';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import {
  BaseWatchList,
  MixpanelEvent,
  MixpanelEventSource,
  MixpanelSourceContext,
  SecurityType,
  useCallbackRef,
  useMixpanel,
  type WatchlistProps,
  type WatchListRowNode,
} from '@talos/kyoko';
import type { CellClickedEvent } from 'ag-grid-community';
import { values } from 'lodash-es';
import { useMemo } from 'react';
import { orderSliceActions } from '../OMS/NewOrder/OrderSlice';
import { openView } from '../OMS/OMSSlice';
import { WatchlistMenuCellRenderer } from './WatchlistMenuCellRenderer';
import { useWatchlistContextMenuItems } from './useWatchlistContextMenuItems';

type AvaWatchlistProps = Omit<WatchlistProps, 'enabledColumns' | 'securityTypeOptions'>;

export const Watchlist = ({ extraGridOptions: propsExtra, ...props }: AvaWatchlistProps) => {
  const { enableCalendarSpreads } = useFeatureFlag();
  const getContextMenuItems = useWatchlistContextMenuItems();
  const watchlistTypeFilters = useMemo(() => {
    const securityTypeFilterOptions = values(SecurityType);
    if (enableCalendarSpreads) {
      return securityTypeFilterOptions;
    }
    return securityTypeFilterOptions.filter(s => s !== SecurityType.CalendarSpread);
  }, [enableCalendarSpreads]);

  const extraGridOptions = useMemo(
    () => ({
      ...propsExtra,
      getContextMenuItems,
      suppressContextMenu: false,
    }),
    [propsExtra, getContextMenuItems]
  );

  const columns = useWatchlistColumns();
  return (
    <MixpanelSourceContext.Provider value={MixpanelEventSource.Watchlist}>
      <ErrorBoundary>
        <BaseWatchList
          enabledColumns={columns}
          extraGridOptions={extraGridOptions}
          securityTypesOptions={watchlistTypeFilters}
          menuCellRenderer={WatchlistMenuCellRenderer}
          {...props}
        />
      </ErrorBoundary>
    </MixpanelSourceContext.Provider>
  );
};

export function useWatchlistColumns() {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();

  const handleCellClicked = useCallbackRef((event: CellClickedEvent) => {
    const node = event.node as WatchListRowNode;
    const security = node.data.security;

    mixpanel.track(MixpanelEvent.PrimeOrder);
    dispatch(openView(OMSView.NewOrderForm));
    dispatch(
      orderSliceActions.primeNewOrderForm({
        symbol: security?.Symbol,
      })
    );
  });

  const columns = useMemo(() => {
    return [
      { type: 'rank' as const },
      { type: 'favorite' as const },
      { type: 'symbol' as const, onClick: handleCellClicked },
      { type: 'refRate' as const, onClick: handleCellClicked },
      { type: 'volume' as const },
      { type: 'sparkline' as const },
      { type: 'menu' as const },
      { type: 'reorder' as const },
    ];
  }, [handleCellClicked]);
  return columns;
}
